import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, Plus, FileText, Send, CheckCircle2, XCircle, Sparkles, Trash2,
  Download, Pencil, RefreshCw, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import {
  listFacilityProposals, createFacilityProposal, updateProposal, deleteProposal,
  regenerateProposalPdf, sendProposal, markProposalAccepted, markProposalDeclined,
  proposalPdfUrl, PROPOSAL_STATUS_META, PROPOSAL_UNITS,
  BILLING_FREQUENCIES, PAYMENT_TERMS, getFacilityLead,
} from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return format(new Date(iso), "PP"); } catch { return iso; }
};
const fmtMoney = (v) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const StatusPill = ({ status }) => {
  const meta = PROPOSAL_STATUS_META[status] || PROPOSAL_STATUS_META.draft;
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
      style={{ background: meta.bg, color: meta.color, borderColor: meta.border }}
      data-testid={`proposal-status-${status}`}
    >
      {meta.label}
    </span>
  );
};

// =============================================================================
export default function FacilityProposalsPanel({ facility, onFacilityUpdate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);       // proposal being edited or "new"
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchAll = async () => {
    if (!facility?.id) return;
    try {
      setLoading(true);
      const list = await listFacilityProposals(facility.id);
      setItems(list);
    } catch {
      toast.error("Failed to load proposals.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [facility?.id]);

  const createDraft = async (seedFromAi) => {
    try {
      setSaving(true);
      const created = await createFacilityProposal(facility.id, { seed_from_ai: !!seedFromAi });
      setItems((prev) => [created, ...prev]);
      setEditing(created);
      toast.success(seedFromAi ? "Draft created from AI analysis." : "Draft proposal created.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create draft.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (prop) => {
    if (!window.confirm(`Delete draft ${prop.proposal_number}?`)) return;
    try {
      setBusyId(prop.id);
      await deleteProposal(prop.id);
      setItems((prev) => prev.filter((p) => p.id !== prop.id));
      toast.success("Draft deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  const handleAccepted = async (prop) => {
    try {
      setBusyId(prop.id);
      const res = await markProposalAccepted(prop.id);
      setItems((prev) => prev.map((p) => (p.id === prop.id ? res.proposal : p)));
      toast.success("Proposal marked accepted.");
      try {
        const fresh = await getFacilityLead(facility.id);
        onFacilityUpdate?.(fresh);
      } catch { /* ignore */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDeclined = async (prop) => {
    try {
      setBusyId(prop.id);
      const res = await markProposalDeclined(prop.id);
      setItems((prev) => prev.map((p) => (p.id === prop.id ? res.proposal : p)));
      toast.success("Proposal marked declined.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to decline.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRegenerate = async (prop) => {
    try {
      setBusyId(prop.id);
      await regenerateProposalPdf(prop.id);
      toast.success("PDF regenerated.");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF generation failed.");
    } finally {
      setBusyId(null);
    }
  };

  const hasAi = Boolean(facility?.ai_analysis?.estimated_monthly_revenue_high);

  return (
    <div className="space-y-4" data-testid="facility-proposals-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-sm font-semibold" style={{ color: "#0B2545" }}>Proposals</div>
          <div className="text-xs" style={{ color: "#64748B" }}>
            Draft, generate PDFs, and email partnership proposals.
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm"
            className="h-8 text-xs" onClick={() => createDraft(true)}
            disabled={saving || !hasAi}
            data-testid="proposal-create-from-ai"
            title={hasAi ? "Draft a proposal pre-populated from the AI analysis" : "Run AI analysis first to enable this"}
          >
            <Sparkles className="h-3.5 w-3.5 mr-1" style={{ color: "#D4AF37" }} />
            New from AI
          </Button>
          <Button
            size="sm" className="h-8 text-xs" onClick={() => createDraft(false)}
            disabled={saving}
            style={{ background: "#0B2545", color: "#FFF" }}
            data-testid="proposal-create-blank"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
            New Draft
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-6 flex items-center gap-2 text-sm" style={{ color: "#94A3B8" }}>
          <Loader2 className="h-4 w-4 animate-spin" /> Loading proposals…
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-lg border border-dashed py-8 text-center text-sm"
          style={{ borderColor: "#E2E8F0", color: "#94A3B8" }}
          data-testid="proposals-empty"
        >
          No proposals yet. Create your first draft above.
        </div>
      ) : (
        <div className="space-y-3" data-testid="proposals-list">
          {items.map((p) => (
            <ProposalCard
              key={p.id} prop={p} busy={busyId === p.id}
              onEdit={() => setEditing(p)}
              onDelete={() => handleDelete(p)}
              onAccept={() => handleAccepted(p)}
              onDecline={() => handleDeclined(p)}
              onRegenerate={() => handleRegenerate(p)}
              onSent={(fresh) => setItems((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x)))}
              onFacilityUpdate={onFacilityUpdate}
            />
          ))}
        </div>
      )}

      {editing && (
        <ProposalEditorDialog
          proposal={editing}
          facility={facility}
          onClose={() => setEditing(null)}
          onSaved={(fresh) => {
            setItems((prev) => prev.map((p) => (p.id === fresh.id ? fresh : p)));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
const ProposalCard = ({ prop, busy, onEdit, onDelete, onAccept, onDecline, onRegenerate, onSent }) => {
  const [showSend, setShowSend] = useState(false);
  const isDraft = prop.status === "draft";
  const isSent = prop.status === "sent";
  const total = useMemo(() => {
    return (prop.pricing_items || []).reduce((acc, it) => {
      return acc + Number(it.quantity || 0) * Number(it.unit_price || 0);
    }, 0);
  }, [prop.pricing_items]);

  return (
    <div
      className="rounded-xl border p-4 space-y-3"
      style={{ borderColor: "#E2E8F0", background: "#FFF" }}
      data-testid={`proposal-card-${prop.proposal_number}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold" style={{ color: "#0B2545" }}>{prop.title}</span>
            <StatusPill status={prop.status} />
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: "#64748B" }}>
            {prop.proposal_number} · Created {fmtDate(prop.created_at)}
            {prop.sent_at ? ` · Sent ${fmtDate(prop.sent_at)}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {prop.pdf_file_id ? (
            <a
              href={proposalPdfUrl(prop.id)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md border px-2.5 h-8 text-xs font-medium"
              style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
              data-testid={`proposal-view-pdf-${prop.proposal_number}`}
            >
              <FileText className="h-3.5 w-3.5" /> View PDF <ExternalLink className="h-3 w-3" />
            </a>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <Cell label="Est. Monthly" value={fmtMoney(prop.estimated_monthly_revenue)} accent />
        <Cell label="Term" value={`${prop.contract_months || 12} mo`} />
        <Cell label="Billing" value={(prop.billing_frequency || "monthly").replace("_", " ")} />
        <Cell label="Line Total" value={fmtMoney(total)} />
      </div>

      {prop.pricing_items?.length ? (
        <div className="text-[11px]" style={{ color: "#475569" }}>
          {prop.pricing_items.length} line item{prop.pricing_items.length === 1 ? "" : "s"}
          {" · "}
          {(prop.services || []).slice(0, 3).join(", ")}
          {(prop.services || []).length > 3 ? ` +${prop.services.length - 3}` : ""}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {isDraft && (
          <>
            <Button
              size="sm" variant="outline" className="h-8 text-xs"
              onClick={onEdit} data-testid={`proposal-edit-${prop.proposal_number}`}
            >
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
            <Button
              size="sm" variant="outline" className="h-8 text-xs"
              onClick={onRegenerate} disabled={busy}
              data-testid={`proposal-generate-pdf-${prop.proposal_number}`}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Generate PDF
            </Button>
            <Button
              size="sm" className="h-8 text-xs"
              style={{ background: "#0B2545", color: "#FFF" }}
              onClick={() => setShowSend(true)}
              data-testid={`proposal-send-${prop.proposal_number}`}
            >
              <Send className="h-3.5 w-3.5 mr-1" /> Send to Facility
            </Button>
            <Button
              size="sm" variant="outline" className="h-8 text-xs"
              style={{ color: "#991B1B", borderColor: "rgba(220,38,38,0.3)" }}
              onClick={onDelete} disabled={busy}
              data-testid={`proposal-delete-${prop.proposal_number}`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
          </>
        )}
        {isSent && (
          <>
            <Button
              size="sm" className="h-8 text-xs"
              style={{ background: "#065F46", color: "#FFF" }}
              onClick={onAccept} disabled={busy}
              data-testid={`proposal-accept-${prop.proposal_number}`}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark Accepted
            </Button>
            <Button
              size="sm" variant="outline" className="h-8 text-xs"
              onClick={onDecline} disabled={busy}
              data-testid={`proposal-decline-${prop.proposal_number}`}
            >
              <XCircle className="h-3.5 w-3.5 mr-1" /> Mark Declined
            </Button>
            <Button
              size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => setShowSend(true)}
              data-testid={`proposal-resend-${prop.proposal_number}`}
            >
              <Send className="h-3.5 w-3.5 mr-1" /> Resend
            </Button>
          </>
        )}
        {prop.pdf_file_id ? (
          <a
            href={proposalPdfUrl(prop.id)} download
            className="inline-flex items-center gap-1 rounded-md border px-2.5 h-8 text-xs font-medium"
            style={{ borderColor: "#E2E8F0", color: "#475569" }}
            data-testid={`proposal-download-${prop.proposal_number}`}
          >
            <Download className="h-3.5 w-3.5" /> Download
          </a>
        ) : null}
      </div>

      {showSend && (
        <SendProposalDialog
          prop={prop}
          onClose={() => setShowSend(false)}
          onSent={(fresh) => { onSent(fresh); setShowSend(false); }}
        />
      )}
    </div>
  );
};

const Cell = ({ label, value, accent }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#94A3B8" }}>{label}</div>
    <div className="mt-0.5 text-sm font-semibold" style={{ color: accent ? "#7A5E12" : "#0B2545" }}>
      {value || "—"}
    </div>
  </div>
);

// =============================================================================
// Send dialog
// =============================================================================
const SendProposalDialog = ({ prop, onClose, onSent }) => {
  const [customMessage, setCustomMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const send = async () => {
    try {
      setSending(true);
      setError("");
      const res = await sendProposal(prop.id, { custom_message: customMessage || undefined });
      toast.success("Proposal emailed with PDF attached.");
      onSent(res.proposal);
    } catch (e) {
      setError(e?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="proposal-send-dialog">
        <DialogHeader>
          <DialogTitle>Send proposal · {prop.proposal_number}</DialogTitle>
          <DialogDescription>
            The proposal PDF will be attached and delivered via Resend. Delivery status will appear in the Activity tab.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error ? (
            <div
              className="rounded-lg border p-3 text-xs"
              style={{ borderColor: "rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#991B1B" }}
              data-testid="proposal-send-error"
            >
              {error}
            </div>
          ) : null}
          <div>
            <Label>Add a short personal note (optional)</Label>
            <Textarea
              rows={4}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Hi Sarah — thanks for the great call yesterday. As discussed…"
              className="mt-1"
              data-testid="proposal-send-message"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
          <Button
            onClick={send} disabled={sending} className="h-9"
            style={{ background: "#0B2545", color: "#FFF" }}
            data-testid="proposal-send-confirm"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
            Send proposal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// Editor dialog (draft only)
// =============================================================================
const ProposalEditorDialog = ({ proposal, facility, onClose, onSaved }) => {
  const isDraft = proposal.status === "draft";
  const [form, setForm] = useState(() => ({
    title: proposal.title || "",
    services: (proposal.services || []).join(", "),
    contract_months: proposal.contract_months || 12,
    billing_frequency: proposal.billing_frequency || "monthly",
    payment_terms: proposal.payment_terms || "net_30",
    estimated_monthly_revenue: proposal.estimated_monthly_revenue ?? "",
    valid_until: proposal.valid_until || "",
    scope_of_work: proposal.scope_of_work || "",
    terms_notes: proposal.terms_notes || "",
    pricing_model: proposal.pricing_model || "",
  }));
  const [items, setItems] = useState(() => (proposal.pricing_items || []).map((it) => ({
    description: it.description || "",
    quantity: it.quantity ?? 1,
    unit: it.unit || "each",
    unit_price: it.unit_price ?? 0,
    notes: it.notes || "",
  })));
  const [saving, setSaving] = useState(false);

  const addItem = () => setItems((prev) => [
    ...prev, { description: "", quantity: 1, unit: "per_month", unit_price: 0, notes: "" },
  ]);
  const updateItem = (idx, key, val) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const totalLine = useMemo(() => items.reduce((a, it) => a + Number(it.quantity || 0) * Number(it.unit_price || 0), 0), [items]);

  const save = async () => {
    try {
      setSaving(true);
      const payload = {
        title: form.title || undefined,
        services: form.services
          ? form.services.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        pricing_items: items.map((it) => ({
          description: it.description || "Line item",
          quantity: Number(it.quantity || 1),
          unit: it.unit || "each",
          unit_price: Number(it.unit_price || 0),
          notes: it.notes || undefined,
        })),
        contract_months: Number(form.contract_months) || 12,
        billing_frequency: form.billing_frequency || undefined,
        payment_terms: form.payment_terms || undefined,
        pricing_model: form.pricing_model || undefined,
        estimated_monthly_revenue: form.estimated_monthly_revenue === ""
          ? null : Number(form.estimated_monthly_revenue),
        valid_until: form.valid_until || null,
        scope_of_work: form.scope_of_work || undefined,
        terms_notes: form.terms_notes || undefined,
      };
      const fresh = await updateProposal(proposal.id, payload);
      toast.success("Proposal saved.");
      onSaved(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="proposal-editor-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b" style={{ borderColor: "#F1F5F9" }}>
          <DialogTitle>Edit proposal · {proposal.proposal_number}</DialogTitle>
          <DialogDescription>
            {isDraft
              ? `Editing a draft for ${facility?.facility_name || "this facility"}. PDF will regenerate on save.`
              : "This proposal has been sent and is read-only. Clone it into a new draft to change pricing."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6" data-testid="proposal-editor-body">
          {/* Overview */}
          <section className="space-y-3">
            <SectionTitle>Overview</SectionTitle>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Title">
                <Input
                  value={form.title} disabled={!isDraft}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="proposal-field-title"
                />
              </Field>
              <Field label="Services (comma-separated)">
                <Input
                  value={form.services} disabled={!isDraft}
                  onChange={(e) => setForm({ ...form, services: e.target.value })}
                  data-testid="proposal-field-services"
                />
              </Field>
              <Field label="Contract months">
                <Input
                  type="number" min="1" max="60"
                  value={form.contract_months} disabled={!isDraft}
                  onChange={(e) => setForm({ ...form, contract_months: e.target.value })}
                  data-testid="proposal-field-months"
                />
              </Field>
              <Field label="Estimated monthly revenue (USD)">
                <Input
                  type="number" min="0" step="50"
                  value={form.estimated_monthly_revenue} disabled={!isDraft}
                  onChange={(e) => setForm({ ...form, estimated_monthly_revenue: e.target.value })}
                  data-testid="proposal-field-est-monthly"
                />
              </Field>
              <Field label="Billing frequency">
                <Select
                  value={form.billing_frequency} disabled={!isDraft}
                  onValueChange={(v) => setForm({ ...form, billing_frequency: v })}
                >
                  <SelectTrigger className="h-10" data-testid="proposal-field-billing"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BILLING_FREQUENCIES.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Payment terms">
                <Select
                  value={form.payment_terms} disabled={!isDraft}
                  onValueChange={(v) => setForm({ ...form, payment_terms: v })}
                >
                  <SelectTrigger className="h-10" data-testid="proposal-field-terms"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TERMS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Pricing model">
                <Input
                  value={form.pricing_model} disabled={!isDraft}
                  onChange={(e) => setForm({ ...form, pricing_model: e.target.value })}
                  placeholder="e.g. Per Draw + Bundled Services"
                  data-testid="proposal-field-model"
                />
              </Field>
              <Field label="Valid until (date)">
                <Input
                  type="date" value={form.valid_until} disabled={!isDraft}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  data-testid="proposal-field-valid-until"
                />
              </Field>
            </div>
          </section>

          {/* Line items */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionTitle>Pricing line items</SectionTitle>
              {isDraft && (
                <Button
                  variant="outline" size="sm" className="h-7 text-xs"
                  onClick={addItem} data-testid="proposal-add-line"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add line
                </Button>
              )}
            </div>
            {items.length === 0 ? (
              <div className="text-xs py-4 text-center rounded border border-dashed" style={{ borderColor: "#E2E8F0", color: "#94A3B8" }}>
                No line items yet.
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div
                    key={idx}
                    className="grid gap-2 grid-cols-12 items-center rounded-md border px-2 py-2"
                    style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}
                    data-testid={`proposal-line-${idx}`}
                  >
                    <Input
                      className="col-span-12 sm:col-span-5 h-8 text-xs"
                      placeholder="Description"
                      value={it.description} disabled={!isDraft}
                      onChange={(e) => updateItem(idx, "description", e.target.value)}
                      data-testid={`proposal-line-desc-${idx}`}
                    />
                    <Input
                      type="number" min="0" step="1"
                      className="col-span-3 sm:col-span-1 h-8 text-xs"
                      placeholder="Qty" value={it.quantity} disabled={!isDraft}
                      onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                      data-testid={`proposal-line-qty-${idx}`}
                    />
                    <div className="col-span-4 sm:col-span-2">
                      <Select
                        value={it.unit} disabled={!isDraft}
                        onValueChange={(v) => updateItem(idx, "unit", v)}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`proposal-line-unit-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PROPOSAL_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      type="number" min="0" step="0.01"
                      className="col-span-4 sm:col-span-2 h-8 text-xs"
                      placeholder="Unit price" value={it.unit_price} disabled={!isDraft}
                      onChange={(e) => updateItem(idx, "unit_price", e.target.value)}
                      data-testid={`proposal-line-price-${idx}`}
                    />
                    <div className="col-span-8 sm:col-span-1 text-right text-xs font-semibold" style={{ color: "#0B2545" }}>
                      {fmtMoney(Number(it.quantity || 0) * Number(it.unit_price || 0))}
                    </div>
                    {isDraft && (
                      <button
                        onClick={() => removeItem(idx)}
                        className="col-span-4 sm:col-span-1 h-8 rounded flex items-center justify-center"
                        style={{ color: "#991B1B" }}
                        data-testid={`proposal-line-remove-${idx}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                <div className="flex items-center justify-end gap-3 text-xs pt-1">
                  <span style={{ color: "#64748B" }}>Line total:</span>
                  <span className="text-sm font-semibold" style={{ color: "#0B2545" }}>{fmtMoney(totalLine)}</span>
                </div>
              </div>
            )}
          </section>

          {/* Scope */}
          <section className="space-y-3">
            <SectionTitle>Scope of work</SectionTitle>
            <Textarea
              rows={4} value={form.scope_of_work} disabled={!isDraft}
              onChange={(e) => setForm({ ...form, scope_of_work: e.target.value })}
              data-testid="proposal-field-scope"
            />
          </section>

          {/* Terms */}
          <section className="space-y-3">
            <SectionTitle>Terms & agreement</SectionTitle>
            <Textarea
              rows={5} value={form.terms_notes} disabled={!isDraft}
              onChange={(e) => setForm({ ...form, terms_notes: e.target.value })}
              data-testid="proposal-field-terms-body"
            />
          </section>
        </div>

        <DialogFooter className="px-6 py-4 border-t" style={{ borderColor: "#F1F5F9" }}>
          <Button variant="outline" onClick={onClose} className="h-9">Close</Button>
          {isDraft && (
            <Button
              onClick={save} disabled={saving} className="h-9"
              style={{ background: "#0B2545", color: "#FFF" }}
              data-testid="proposal-editor-save"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Save draft
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SectionTitle = ({ children }) => (
  <div
    className="text-[10px] uppercase tracking-[0.18em] font-semibold"
    style={{ color: "#475569" }}
  >
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
